import * as THREE from 'three';
import type { GeoObjectMeshOptions, Materials } from '@visualizer/types';
import { PreProcessingTypes } from '@visualizer/types';
import { GeoObjectSingleMesh } from '@data/geodata/GeoObjectSingleMesh';
import {
    createDefaultFloorMaterial,
    createDefaultRoofMaterial,
    createDefaultWallMaterial,
    updateProjectSketchSensorDistances,
} from './utils';

type SwisstopoTypes = [PreProcessingTypes.SWISSTOPO_ROOF, PreProcessingTypes.SWISSTOPO_WALL, PreProcessingTypes.SWISSTOPO_FLOOR];

export class SwisstopoSingleMesh extends GeoObjectSingleMesh<SwisstopoTypes> {
    constructor({ name, colors, data, mode, thresholdAngle }: Omit<GeoObjectMeshOptions<SwisstopoTypes>, 'types'>) {
        super({
            name,
            colors,
            mode,
            types: [PreProcessingTypes.SWISSTOPO_ROOF, PreProcessingTypes.SWISSTOPO_WALL, PreProcessingTypes.SWISSTOPO_FLOOR],
            thresholdAngle,
            data,
        });
    }

    protected override createMaterials(): Materials<SwisstopoTypes> {
        return {
            swisstopoFloor: createDefaultFloorMaterial(),
            swisstopoRoof: createDefaultRoofMaterial(),
            swisstopoWall: createDefaultWallMaterial(),
        };
    }
}

type CustomObstructionTypes = [
    PreProcessingTypes.CUSTOM_BUILDINGS_ROOF,
    PreProcessingTypes.CUSTOM_BUILDINGS_WALL,
    PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR,
];

export class CustomObstructionSingleMesh extends GeoObjectSingleMesh<CustomObstructionTypes> {
    constructor({ name, colors, data, mode, thresholdAngle }: Omit<GeoObjectMeshOptions<CustomObstructionTypes>, 'types'>) {
        super({
            name,
            colors,
            mode,
            types: [
                PreProcessingTypes.CUSTOM_BUILDINGS_ROOF,
                PreProcessingTypes.CUSTOM_BUILDINGS_WALL,
                PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR,
            ],
            thresholdAngle,
            data,
        });
    }
    protected override createMaterials(): Materials<CustomObstructionTypes> {
        return {
            customBuildingsFloor: createDefaultFloorMaterial(),
            customBuildingsRoof: createDefaultRoofMaterial(),
            customBuildingsWall: createDefaultWallMaterial(),
        };
    }

    hasVisibleBuildings(): boolean {
        const geom = this.meshes.geometry as THREE.BufferGeometry | undefined;
        return Boolean(geom?.getAttribute?.('position')?.count);
    }
}

type ProjectSketchTypes = [
    PreProcessingTypes.PROJECT_SKETCH_ROOF,
    PreProcessingTypes.PROJECT_SKETCH_WALL,
    PreProcessingTypes.PROJECT_SKETCH_FLOOR,
];

export class ProjectSketchSingleMesh extends GeoObjectSingleMesh<ProjectSketchTypes> {
    constructor({ name, colors, data, mode, thresholdAngle }: Omit<GeoObjectMeshOptions<ProjectSketchTypes>, 'types'>) {
        super({
            name,
            colors,
            mode,
            types: [
                PreProcessingTypes.PROJECT_SKETCH_ROOF,
                PreProcessingTypes.PROJECT_SKETCH_WALL,
                PreProcessingTypes.PROJECT_SKETCH_FLOOR,
            ],
            thresholdAngle,
            data,
        });
        updateProjectSketchSensorDistances(data);
    }
    protected override createMaterials(): Materials<ProjectSketchTypes> {
        return {
            projectSketchFloor: createDefaultFloorMaterial(),
            projectSketchRoof: createDefaultRoofMaterial(),
            projectSketchWall: createDefaultWallMaterial(),
        };
    }

    hasVisibleBuildings(): boolean {
        const geom = this.meshes.geometry as THREE.BufferGeometry | undefined;
        return Boolean(geom?.getAttribute?.('position')?.count);
    }
}
